import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import coin1Video from "../../../assets/videos/coin1.mp4";
import coin2Video from "../../../assets/videos/coin2.mp4";
import spin1Video from "../../../assets/videos/spin1.mp4";
import spin2Video from "../../../assets/videos/spin2.mp4";
import coinSuccessImage from "../../../assets/images/coin_success.png";
import spinSuccessImage from "../../../assets/images/spin_success.png";
import coinPosterImage from "../../../assets/images/coin_poster.jpg";
import styles from "./styles.module.css";
import { LanguageContext } from "../../../context/LanguageContext";
import { handleApi } from "../../../apis";
import { GET_UDHAAR_WIN, POST_UDHAAR_WIN } from "../../../apis/configuration";
import { useSnackbar } from "../../../context/SnackbarContext";
import { analytics, triggerLogEvent } from "../../../config/firebase";

// Test A - coin flow: coin1 (loop, tappable) -> coin2 (auto) -> API
const COIN_STAGE = {
  IDLE: "idle",
  SPINNING: "spinning",
};

const COIN_STAGE_VIDEO = {
  [COIN_STAGE.IDLE]: coin1Video,
  [COIN_STAGE.SPINNING]: coin2Video,
};

// Test B - spin flow: spin1 (loop, tappable) -> spin2 (auto) -> API
const SPIN_STAGE = {
  IDLE: "idle",
  REVEAL: "reveal",
};

// The page is only ever opened on phones, so there are no tablet variants —
// a width-based switch also misfired on large iPhones (428-440px wide).
const SPIN_STAGE_VIDEO = {
  [SPIN_STAGE.IDLE]: spin1Video,
  [SPIN_STAGE.REVEAL]: spin2Video,
};

const TEST_VARIANT = {
  COIN: "coin",
  SPIN: "spin",
};

// Comes from the ?game_type= query param the route is opened with.
const GAME_TYPE = {
  COIN_FLIP: "COIN_FLIP",
  SPIN_WHEEL: "SPIN_WHEEL",
};

// Deep links the "VIEW POINTS" button on the success screen opens, per variant.
const VIEW_POINTS_URL = {
  [TEST_VARIANT.COIN]: "https://udhaar.onelink.me/QrKU/bzwwj625",
  [TEST_VARIANT.SPIN]: "https://udhaar.onelink.me/QrKU/jp7nbw77",
};

const claimWin = (token, navigate) =>
  handleApi(POST_UDHAAR_WIN(token), null, null, navigate);

// The backend rejects a second claim on the same token with a 400:
// {"message":"You have already claimed the reward","success":false}.
// The reward was already granted, so after showing that message the user
// still lands on the success screen to reach VIEW POINTS.
const isAlreadyClaimedError = (error) =>
  error?.response?.data?.success === false &&
  error?.response?.data?.message === "You have already claimed the reward";

// The token lookup rejects an unrecognized/expired token the same way:
// {"message":"Invalid Token","success":false}.
const isInvalidTokenError = (error) =>
  error?.response?.data?.success === false &&
  error?.response?.data?.message === "Invalid Token";

// No response at all (offline, DNS failure, timeout, etc.) vs. a real
// server-side rejection like the two above.
const isNetworkError = (error) => !error?.response;

const ViewPointsButton = ({ variant, phoneNumber }) => (
  <button
    type="button"
    className={styles.viewPointsBtn}
    onClick={() => {
      triggerLogEvent(analytics, "app_CTA", { variant, phone_number: phoneNumber });
      window.location.href = VIEW_POINTS_URL[variant];
    }}
  >
    VIEW POINTS
  </button>
);

// Plays whichever stage is active and pauses the rest; every video in
// stageVideoRef stays mounted/preloaded so switching stages is an instant
// crossfade instead of a reload/black-frame glitch.
const usePlayActiveVideo = (stage, stageVideoRef) => {
  useEffect(() => {
    Object.entries(stageVideoRef).forEach(([videoStage, ref]) => {
      const videoEl = ref.current;
      if (!videoEl) return;

      if (videoStage === stage) {
        videoEl.currentTime = 0;
        videoEl.play().catch(() => { });
      } else {
        videoEl.pause();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // iOS refuses play() without a user gesture when autoplay is blocked (Low
  // Power Mode — common on older iPhones like the X), leaving the video
  // stuck. Retry on the first tap anywhere so it starts as soon as the user
  // touches the screen. Skips a video that already played to its end.
  useEffect(() => {
    const retryPlay = () => {
      const videoEl = stageVideoRef[stage]?.current;
      if (videoEl && videoEl.paused && !videoEl.ended) {
        videoEl.play().catch(() => { });
      }
    };
    document.addEventListener("touchend", retryPlay, { passive: true });
    document.addEventListener("click", retryPlay);
    return () => {
      document.removeEventListener("touchend", retryPlay);
      document.removeEventListener("click", retryPlay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
};

// Native `loop` restarts a video by stopping and reinitializing the decoder
// at the seam, which shows up as a visible pause/stutter on some encodes.
// Jumping back a hair before the true end skips that restart entirely, so
// the wrap feels continuous instead of "stop then go".
const LOOP_REWIND_THRESHOLD_SECONDS = 0.15;

// `timeupdate` only fires a few times a second, so at a slowed-down
// playbackRate the rewind can land after the true end — native `loop` then
// wins the race and the visible stutter comes back. Polling every animation
// frame instead (~60fps of real time, regardless of playbackRate) makes the
// rewind land reliably before the seam.
const useSeamlessLoop = (videoRef) => {
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;

    let rafId;

    const tick = () => {
      if (
        videoEl.duration &&
        !videoEl.paused &&
        !videoEl.seeking &&
        videoEl.currentTime > videoEl.duration - LOOP_REWIND_THRESHOLD_SECONDS
      ) {
        videoEl.currentTime = 0;
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [videoRef]);
};

// Upper bound on how long a tap waits for the next video to buffer before
// switching anyway, so a slow or stuck load never makes the tap look dead.
const STAGE_READY_TIMEOUT_MS = 1500;

// Switches `stage` once the target video can start playing. Until then the
// current stage keeps playing, so we don't cut to a black/frozen frame.
//
// iOS Safari ignores preload="auto" and never downloads a paused, hidden
// video, so waiting on canplay/canplaythrough alone hung forever on iPhone
// and the tap appeared to do nothing. Calling play() here — inside the tap's
// user gesture, on a muted video — forces iOS to start buffering it (it stays
// invisible until the stage switches, and usePlayActiveVideo then restarts it
// from 0). The timeout is a last-resort fallback.
const useStageController = (initialStage, stageVideoRef) => {
  const [stage, setStage] = useState(initialStage);

  const requestStage = (nextStage) => {
    const videoEl = stageVideoRef[nextStage]?.current;
    if (!videoEl || videoEl.readyState >= 3) {
      setStage(nextStage);
      return;
    }

    let settled = false;
    const handleReady = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      videoEl.removeEventListener("canplay", handleReady);
      videoEl.removeEventListener("canplaythrough", handleReady);
      setStage(nextStage);
    };
    videoEl.addEventListener("canplay", handleReady);
    videoEl.addEventListener("canplaythrough", handleReady);
    const timeoutId = setTimeout(handleReady, STAGE_READY_TIMEOUT_MS);

    videoEl.play().catch(() => { });
  };

  return [stage, requestStage];
};

const CoinFlow = ({ token, phoneNumber }) => {
  const navigate = useNavigate();
  const [I18n] = useContext(LanguageContext);
  const { showSnackbar } = useSnackbar();
  const [isClaiming, setIsClaiming] = useState(false);
  const [hasClaimed, setHasClaimed] = useState(false);

  const idleVideoRef = useRef(null);
  const spinningVideoRef = useRef(null);

  const stageVideoRef = {
    [COIN_STAGE.IDLE]: idleVideoRef,
    [COIN_STAGE.SPINNING]: spinningVideoRef,
  };

  const [stage, requestStage] = useStageController(
    COIN_STAGE.IDLE,
    stageVideoRef,
  );
  usePlayActiveVideo(stage, stageVideoRef);
  useSeamlessLoop(idleVideoRef);

  useEffect(() => {
  if (idleVideoRef.current) {
    idleVideoRef.current.playbackRate = 0.4;
  }
}, []);
  const handleCoinTap = () => {
    if (stage === COIN_STAGE.IDLE) {
      requestStage(COIN_STAGE.SPINNING);
    }
  };

  const handleSpinningEnded = async () => {
    setIsClaiming(true);
    try {
      await claimWin(token, navigate);
      setHasClaimed(true);
      triggerLogEvent(analytics, "game_played", {
        variant: TEST_VARIANT.COIN,
        phone_number: phoneNumber,
      });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        I18n.something_went_wrong_please_try_again;
      showSnackbar({ message, severity: "error", icon: "" });
      if (isAlreadyClaimedError(error)) {
        setHasClaimed(true);
      }
    } finally {
      setIsClaiming(false);
    }
  };

  if (hasClaimed) {
    return (
      <Box className={styles.successScreen}>
        <img
          src={coinSuccessImage}
          alt=""
          className={`${styles.successImage} ${styles.coinFit}`}
        />
        <ViewPointsButton variant={TEST_VARIANT.COIN} phoneNumber={phoneNumber} />
      </Box>
    );
  }


  return (
    <Box className={styles.videoStack}>
      <video
        ref={idleVideoRef}
        className={`${styles.video} ${styles.coinFit} ${styles.tappable} ${stage === COIN_STAGE.IDLE ? styles.videoActive : ""}`}
        src={COIN_STAGE_VIDEO[COIN_STAGE.IDLE]}
        poster={coinPosterImage}
        muted
        playsInline
        loop
        preload="auto"
        onClick={handleCoinTap}
      />
      <video
        ref={spinningVideoRef}
        className={`${styles.video} ${styles.coinFit} ${stage === COIN_STAGE.SPINNING ? styles.videoActive : ""}`}
        src={COIN_STAGE_VIDEO[COIN_STAGE.SPINNING]}
        poster={coinPosterImage}
        muted
        playsInline
        preload="auto"
        onEnded={handleSpinningEnded}
      />
      {isClaiming && (
        <Box className={styles.claimingOverlay}>
          <CircularProgress size={36} sx={{ color: "#fff" }} />
        </Box>
      )}
    </Box>
  );
};

const SpinFlow = ({ token, phoneNumber }) => {
  const navigate = useNavigate();
  const [I18n] = useContext(LanguageContext);
  const { showSnackbar } = useSnackbar();
  const [isClaiming, setIsClaiming] = useState(false);
  const [hasClaimed, setHasClaimed] = useState(false);

  const idleVideoRef = useRef(null);
  const revealVideoRef = useRef(null);

  const stageVideoRef = {
    [SPIN_STAGE.IDLE]: idleVideoRef,
    [SPIN_STAGE.REVEAL]: revealVideoRef,
  };

  const [stage, requestStage] = useStageController(
    SPIN_STAGE.IDLE,
    stageVideoRef,
  );
  usePlayActiveVideo(stage, stageVideoRef);

  const handleSpinTap = () => {
    if (stage === SPIN_STAGE.IDLE) {
      requestStage(SPIN_STAGE.REVEAL);
    }
  };

  const handleRevealEnded = async () => {
    setIsClaiming(true);
    try {
      await claimWin(token, navigate);
      setHasClaimed(true);
      triggerLogEvent(analytics, "game_played", {
        variant: TEST_VARIANT.SPIN,
        phone_number: phoneNumber,
      });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        I18n.something_went_wrong_please_try_again;
      showSnackbar({ message, severity: "error", icon: "" });
      if (isAlreadyClaimedError(error)) {
        setHasClaimed(true);
      }
    } finally {
      setIsClaiming(false);
    }
  };

  if (hasClaimed) {
    return (
      <Box className={styles.successScreen}>
        <img src={spinSuccessImage} alt="" className={styles.successImage} />
        <ViewPointsButton variant={TEST_VARIANT.SPIN} phoneNumber={phoneNumber} />
      </Box>
    );
  }

  return (
    <Box className={styles.spinVideoStack}>
      <video
        ref={idleVideoRef}
        className={`${styles.spinVideo} ${styles.tappable} ${stage === SPIN_STAGE.IDLE ? styles.spinVideoActive : ""}`}
        src={SPIN_STAGE_VIDEO[SPIN_STAGE.IDLE]}
        muted
        playsInline
        preload="auto"
        loop
        onClick={handleSpinTap}
      />
      <video
        ref={revealVideoRef}
        className={`${styles.spinVideo} ${stage === SPIN_STAGE.REVEAL ? styles.spinVideoActive : ""}`}
        src={SPIN_STAGE_VIDEO[SPIN_STAGE.REVEAL]}
        muted
        playsInline
        preload="auto"
        onEnded={handleRevealEnded}
      />
      {isClaiming && (
        <Box className={styles.claimingOverlay}>
          <CircularProgress size={36} sx={{ color: "#fff" }} />
        </Box>
      )}
    </Box>
  );
};

// Page-level states for the phone-number lookup that gates the whole screen.
const PAGE_STATUS = {
  LOADING: "loading",
  READY: "ready",
  NETWORK_ERROR: "network_error",
};

// Exposes the wrapper's real rendered height as --game-h, so the CSS can size
// the coin artwork against the visible screen. 100vh/100dvh can't be trusted
// for that math: 100vh is taller than the visible area in iOS Safari, and
// dvh only exists on iOS 15.4+ (iPhone X devices are often older).
const useGameHeightVar = (wrapperRef, deps) => {
  useEffect(() => {
    const update = () => {
      const el = wrapperRef.current;
      if (el) el.style.setProperty("--game-h", `${el.clientHeight}px`);
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

const CoinAnimationScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [I18n] = useContext(LanguageContext);
  const { showSnackbar } = useSnackbar();

  // ?token=...&game_type=COIN_FLIP|SPIN_WHEEL
  const token = searchParams.get("token");
  const gameType = searchParams.get("game_type");

  const variant =
    gameType === GAME_TYPE.SPIN_WHEEL ? TEST_VARIANT.SPIN : TEST_VARIANT.COIN;

  const [phoneNumber, setPhoneNumber] = useState("");
  const [pageStatus, setPageStatus] = useState(
    token ? PAGE_STATUS.LOADING : PAGE_STATUS.READY,
  );

  const wrapperRef = useRef(null);
  useGameHeightVar(wrapperRef, [pageStatus]);

  // Looks up the phone number behind the token so "page_view" (and the later
  // game_played/app_CTA events) can carry it. The game screen stays behind a
  // loader until this resolves so it never renders without the phone number —
  // an invalid token bounces the user back with a toast, and a network error
  // replaces the game with a "try again" screen instead of showing it broken.
  const loadPhoneNumber = () => {
    setPageStatus(PAGE_STATUS.LOADING);
    handleApi(GET_UDHAAR_WIN(token), null, null, navigate)
      .then((resp) => {
        setPhoneNumber(resp?.phone_number);
        triggerLogEvent(analytics, "page_view", {
          phone_number: resp?.phone_number,
          variant,
        });
        setPageStatus(PAGE_STATUS.READY);
      })
      .catch((error) => {
        if (isInvalidTokenError(error)) {
          showSnackbar({
            message: error.response.data.message,
            severity: "error",
          });
          navigate("/");
          return;
        }
        if (isNetworkError(error)) {
          setPageStatus(PAGE_STATUS.NETWORK_ERROR);
          return;
        }
        setPageStatus(PAGE_STATUS.READY);
      });
  };

  useEffect(() => {
    if (!token) return;
    loadPhoneNumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (pageStatus === PAGE_STATUS.NETWORK_ERROR) {
    return (
      <div className={styles.wrapper}>
        <Box className={styles.retryScreen}>
          <p className={styles.retryText}>
            {I18n.something_went_wrong_please_try_again}
          </p>
          <button
            type="button"
            className={styles.viewPointsBtn}
            onClick={loadPhoneNumber}
          >
            TRY AGAIN
          </button>
        </Box>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      {variant === TEST_VARIANT.COIN ? (
        <CoinFlow token={token} phoneNumber={phoneNumber} />
      ) : (
        <SpinFlow token={token} phoneNumber={phoneNumber} />
      )}
      {pageStatus === PAGE_STATUS.LOADING && (
        <Box className={styles.pageLoaderOverlay}>
          <CircularProgress size={36} sx={{ color: "#fff" }} />
        </Box>
      )}
    </div>
  );
};

export default CoinAnimationScreen;
